import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { DrinkCoffeeRegular, FoodAppleRegular, FoodRegular } from '@fluentui/react-icons';
import type { Category } from '../types/catering';
import type { ReactNode } from 'react';
import { selectedControlIconStyles, selectedControlStyles } from './controlStyles';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    overflowX: 'auto',
    paddingBottom: tokens.spacingVerticalXXS,
  },
  chip: {
    flexShrink: '0',
    width: '4.5rem',
    minHeight: '4.5rem',
    padding: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusLarge,
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightRegular,
  },
  icon: {
    display: 'inline-flex',
    fontSize: '1.5rem',
    color: tokens.colorNeutralForeground1,
  },
  selectedIcon: selectedControlIconStyles,
});

function categoryIcon(icon?: string): ReactNode {
  switch (icon) {
    case 'Vegetarian':
      return <FoodAppleRegular aria-hidden="true" />;
    case 'Drinks':
      return <DrinkCoffeeRegular aria-hidden="true" />;
    case 'Snacks':
      return <FoodAppleRegular aria-hidden="true" />;
    default:
      return <FoodRegular aria-hidden="true" />;
  }
}

function categoryIconKey(category: Category): string | undefined {
  if (category.icon) {
    return category.icon;
  }

  switch (category.name.trim().toLowerCase()) {
    case 'vegetarian':
      return 'Vegetarian';
    case 'drink':
    case 'drinks':
    case 'beverage':
    case 'beverages':
      return 'Drinks';
    case 'snack':
    case 'snacks':
    case 'snacks & desserts':
      return 'Snacks';
    case 'fast food':
      return 'FastFood';
    default:
      return undefined;
  }
}

export type CategoryChipsProps = {
  categories: Category[];
  selectedId: number | null;
  onSelect: (categoryId: number | null) => void;
};

export function CategoryChips({ categories, selectedId, onSelect }: CategoryChipsProps) {
  const styles = useStyles();
  const visualSelectedId = selectedId ?? categories[0]?.id ?? null;

  return (
    <div className={styles.root} aria-label="Menu categories">
      {categories.map((category) => (
        (() => {
          const icon = categoryIcon(categoryIconKey(category));

          return (
        <Button
          className={styles.chip}
          style={visualSelectedId === category.id ? selectedControlStyles : undefined}
          appearance="outline"
          key={category.id}
          aria-pressed={visualSelectedId === category.id}
          onClick={() => onSelect(category.id)}
        >
          {icon ? (
            <span
              className={`${styles.icon} ${
                visualSelectedId === category.id ? styles.selectedIcon : ''
              }`}
            >
              {icon}
            </span>
          ) : null}
          {category.name}
        </Button>
          );
        })()
      ))}
    </div>
  );
}
